import React from 'react';
import styled from 'styled-components';

import { Box, Flex, Popover, Text } from 'core/components';
import { Label } from 'app/components/labels/Label';
import { PopoverInteractionKind } from '@blueprintjs/core';

// not the first, not the last, and an even number
function lineBreakCheck(i, last) {
  return i !== 0 && i !== last && i % 2 === 1 && <br />;
}

const getLabelId = (label) =>
  typeof label.get === 'function' ? (label.get('id') ?? label.get('value')) : (label.id ?? label.value);

const LabelListWrapper = styled(Flex)`
  flex-direction: ${(props) => (props.isHorizontal === true ? 'row' : 'column')};
  gap: 4px;
  padding: ${(props) => (props.isHorizontal ? '4px 4px 4px 0' : '4px')};
  align-items: flex-start;
  flex-wrap: ${(props) => (props.wrapLabels ? 'wrap' : 'nowrap')};
`;

/**
 * Displays a list of Labels
 *
 * @param labels labels to display
 * @param wrapLabels wrap labels using standard flex-row rules.
 * @param onRemove adds X button allowing user to remove labels
 * @param priorityLabels labels to prioritize displaying before all others. Mostly relevant when used with
 *                       `model.collection.discreteFilterValues` in order to show user-relevant labels before others.
 * @param shareLevel displays special Kentik labels for Kentik presets
 * @param fixedWidth if true, each label will have a fixed max width, otherwise labels will be sized to fit their content.
 * @param maxLabelsToShow maximum number of labels to display. Shows an indication of how many labels remain.
 * @param renderEmpty renders the label list (i.e. the container box) even if there are no labels to display. Beware of triggering errors with this.
 * @param endElement element to render at end of label list
 * @param isHorizontal if true, labels are displayed in a horizontal row, otherwise they are displayed in a vertical column
 * @param labelMaxWidth maximum width of each label. If not provided, defaults to 250px.
 * @param rest remaining props (mostly expected to be styling) are transferred to the wrapper box
 * @returns {React.JSX.Element|null}
 */
const LabelList = ({
  labels = [],
  wrapLabels,
  onRemove = null,
  priorityLabels = [],
  shareLevel,
  fixedWidth,
  maxLabelsToShow = 2,
  renderEmpty = false,
  endElement = null,
  isHorizontal = true,
  labelMaxWidth,
  labelDecorator = (_, labelComponent) => labelComponent,
  ...rest
}) => {
  if (!renderEmpty && (!Array.isArray(labels) || labels.length === 0)) {
    return null;
  }

  const sortLabels = (a, b) => {
    const idA = getLabelId(a);
    const idB = getLabelId(b);
    // Priority labels come first
    if (priorityLabels.includes(idA)) {
      if (!priorityLabels.includes(idB)) {
        return -1;
      }
    } else if (priorityLabels.includes(idB)) {
      return 1;
    }
    // Then use numeric sort if appropriate
    if (typeof idA === 'number' && typeof idB === 'number') {
      return idA - idB;
    }
    // Otherwise simple string sort
    return String.prototype.localeCompare.call(idA, idB);
  };

  const getStyleOverrides = (hasFixedWidth, widthPercent = 50) =>
    hasFixedWidth ? { maxWidth: `calc(${widthPercent}% - 4px)` } : {};

  const getMoreCount = () => (labels.length ? labels.length - maxLabelsToShow : 0);

  const renderSingleLabel = (label, index, styleOverrides, isFullList = false) => {
    const labelHasGet = typeof label.get === 'function';
    // If a `bg` is provided, use it, otherwise `color` is the bg.
    const bg = labelHasGet ? label.get('bg') : label.bg;
    const color = labelHasGet ? label.get('color') : label.color;
    const labelId = getLabelId(label);
    const testId = `labelId_${labelId}`;
    // display label name if available, otherwise label.label
    const name = labelHasGet ? label.get('name') || label.get('label') : label.name || label.label;

    const onRemoveFn = onRemove && ((event) => onRemove(labelId, event));
    return labelDecorator(
      labelId,
      <Flex {...styleOverrides} key={`${labelId}-${index}`} data-testid="labelWrapper">
        <Label
          bg={bg || color}
          color={bg ? color : undefined}
          name={name}
          onRemove={onRemoveFn}
          shareLevel={shareLevel}
          testId={testId}
          maxWidth={isFullList ? 500 : labelMaxWidth || 250}
        />
        {fixedWidth && lineBreakCheck(index, labels.length - 1)}
      </Flex>
    );
  };

  const renderFullList = (isPopover) => (
    <LabelListWrapper isHorizontal={!isPopover} wrapLabels={wrapLabels}>
      {labels.sort(sortLabels).map((label, i) => renderSingleLabel(label, i, getStyleOverrides(false), isPopover))}
    </LabelListWrapper>
  );

  const renderAbbreviatedList = () => {
    const itemWidth =
      getMoreCount() > 0
        ? 100 / (maxLabelsToShow + 1) // +1 to account for the "+x more" text
        : 100 / labels.length;

    return labels.sort(sortLabels).map((label, i) => {
      if (i >= maxLabelsToShow) {
        return null;
      }
      return renderSingleLabel(label, i, getStyleOverrides(fixedWidth, itemWidth));
    });
  };

  const renderMoreIndicator = () =>
    maxLabelsToShow && labels.length > maxLabelsToShow ? (
      <Flex key="moreIndicator">
        <Text mt="2px">+{labels.length - maxLabelsToShow} more</Text>
      </Flex>
    ) : (
      <></>
    );

  const renderEndElement = () => (endElement ? <span key="endElement">{endElement}</span> : null);

  const renderContent = () => {
    const moreCount = getMoreCount();

    if (wrapLabels) {
      Object.assign(rest, { whiteSpace: 'normal' });
    }

    return moreCount > 0 ? (
      <Popover content={renderFullList(true)} interactionKind={PopoverInteractionKind.HOVER}>
        <Box {...rest}>
          <LabelListWrapper wrapLabels={wrapLabels} isHorizontal={isHorizontal}>
            {renderAbbreviatedList()}
            {renderMoreIndicator()}
            {renderEndElement()}
          </LabelListWrapper>
        </Box>
      </Popover>
    ) : (
      <Box {...rest} width="100%">
        <LabelListWrapper wrapLabels={wrapLabels} isHorizontal={isHorizontal}>
          {renderFullList(false)}
          {renderEndElement()}
        </LabelListWrapper>
      </Box>
    );
  };

  return renderContent();
};

export default LabelList;
